import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChapterEditor() {
  const { id, chapterId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [title, setTitle] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [translationModel, setTranslationModel] = useState('');
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [view, setView] = useState('english'); // 'korean' | 'english' — english is the default since that's what you actually read

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [creatingNext, setCreatingNext] = useState(false);
  const [navigatingPrev, setNavigatingPrev] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [chapterList, setChapterList] = useState([]); // sibling chapters in this novel, ordered

  // translation versions — every translate run is kept, never overwritten
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState('');
  const [viewingVersionId, setViewingVersionId] = useState('');
  const [switchingVersion, setSwitchingVersion] = useState(false);

  // A failed (or still-unfinished) version opened for READING only. It is never
  // activated just by looking at it — a half-translated chapter must not reach
  // the epub looking finished. The English it did produce is shown read-only,
  // and its pieces open below so the failed one can be retried.
  const [previewId, setPreviewId] = useState('');
  const [previewText, setPreviewText] = useState(null);   // null = not previewing
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewIdRef = useRef('');       // mirrors previewId for the polling closure
  const autoPreviewedRef = useRef(false); // open the newest failed version once per chapter

  // background job progress
  const [jobStatus, setJobStatus] = useState(null);   // { status, char_count, model, error_message }
  const pollRef = useRef(null);
  const activeJobRef = useRef(null);

  // long-chapter mode
  const [chunkedMode, setChunkedMode] = useState(false);
  const [chunkSize, setChunkSize] = useState(5000);
  const [chunks, setChunks] = useState([]);
  const [chunkTranslationId, setChunkTranslationId] = useState('');
  const [retryingChunk, setRetryingChunk] = useState('');
  const [openChunkId, setOpenChunkId] = useState('');
  const [chunkDetail, setChunkDetail] = useState(null);
  const [chunkSourceDraft, setChunkSourceDraft] = useState('');
  const [chunkOutputDraft, setChunkOutputDraft] = useState('');
  const [chunkExtraInstruction, setChunkExtraInstruction] = useState('');
  const [savingChunk, setSavingChunk] = useState(false);
  const [savingChunkOutput, setSavingChunkOutput] = useState(false);
  const [showChunks, setShowChunks] = useState(false);

  // fix spacing: the blank line between paragraphs, as a new version
  const [fixingSpacing, setFixingSpacing] = useState(false);
  const [notice, setNotice] = useState('');

  // find & replace
  const [showFind, setShowFind] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    // moving to another chapter: drop anything left over from the last one, so
    // the auto-preview below can't open a version that belongs to it
    previewIdRef.current = '';
    setPreviewId('');
    setPreviewText(null);
    autoPreviewedRef.current = false;
    setVersions([]);
    setActiveVersionId('');
    setViewingVersionId('');
    setChunks([]);
    setShowChunks(false);
    setOpenChunkId('');
    fetchChapter();
    fetchModels();
    fetchChapterList();
    fetchVersions();
    // if a translation was already running when you left, pick it back up —
    // the job lives on the server, so closing the tab never cancels it
    resumeRunningJob();
    return () => stopPolling();
  }, [chapterId]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const describeError = (err) => {
    if (err.response) {
      const d = err.response.data?.detail;
      const detailText = Array.isArray(d)
        ? d.map(x => `${(x.loc || []).join('.')}: ${x.msg}`).join(' | ')
        : (typeof d === 'string' ? d : JSON.stringify(d));
      return `${err.response.status} — ${detailText || 'no detail'}`;
    }
    return err.message || 'network error';
  };

  const fetchChapter = () => {
    setLoading(true);
    axios.get(`${API}/novels/${id}/chapters/${chapterId}?token=${token}`)
      .then(res => {
        const c = res.data.chapter;
        setTitle(c.title || '');
        setSourceText(c.source_text || '');
        setTranslatedText(c.translated_text || '');
        setTranslationModel(c.translation_model || '');
      })
      .catch(err => setErrorMsg(`couldn't load chapter: ${describeError(err)}`))
      .finally(() => setLoading(false));
  };

  const fetchModels = () => {
    axios.get(`${API}/translation-models?token=${token}`)
      .then(res => {
        setModels(res.data.models);
        if (res.data.models.length > 0) {
          const lastUsed = localStorage.getItem('lastTranslationModel');
          const stillValid = lastUsed && res.data.models.some(m => m.id === lastUsed);
          setSelectedModel(stillValid ? lastUsed : res.data.models[0].id);
        }
      })
      .catch(err => console.log(err));
  };

  const fetchChapterList = () => {
    axios.get(`${API}/novels/${id}/chapters?token=${token}`)
      .then(res => setChapterList(res.data.chapters))
      .catch(err => console.log(err));
  };

  const saveChapter = (silent) => {
    setSaving(true);
    setErrorMsg('');
    return axios.put(`${API}/novels/${id}/chapters/${chapterId}`, {
      token, title, source_text: sourceText, translated_text: translatedText,
    })
      .then(() => setSavedAt(new Date()))
      .catch(err => setErrorMsg(`save failed: ${describeError(err)}`))
      .finally(() => setSaving(false));
  };

  // ---- previewing a failed version ----
  const clearPreview = () => {
    previewIdRef.current = '';
    setPreviewId('');
    setPreviewText(null);
  };

  const loadPreviewText = (versionId) => {
    setPreviewLoading(true);
    axios.get(`${API}/novels/${id}/chapters/${chapterId}/translations/${versionId}?token=${token}`)
      .then(res => {
        // ignore a late answer if you've already moved to another version
        if (previewIdRef.current !== versionId) return;
        setPreviewText(res.data.content || '');
      })
      .catch(err => setErrorMsg(`couldn't load that version: ${describeError(err)}`))
      .finally(() => setPreviewLoading(false));
  };

  const previewVersion = (versionId) => {
    const row = versions.find(v => v.id === versionId);
    previewIdRef.current = versionId;
    setPreviewId(versionId);
    setPreviewText('');
    setViewingVersionId(versionId);
    setView('english');
    setErrorMsg('');
    loadPreviewText(versionId);
    // a chunked run: open its pieces straight away, since that's where the
    // failed one is and where it gets retried
    if (row && row.is_chunked) {
      setShowChunks(true);
      setOpenChunkId('');
      fetchChunks(versionId);
    }
  };

  // "use this partial text anyway" — deliberately promotes the partial version
  // to the chapter's text (the server allows it as long as there's some text)
  const activatePreview = () => {
    const vid = previewIdRef.current;
    if (!vid) return;
    switchToVersion(vid);
  };

  const fetchVersions = () => {
    axios.get(`${API}/novels/${id}/chapters/${chapterId}/translations?token=${token}`)
      .then(res => {
        const list = res.data.translations || [];
        setVersions(list);
        const active = res.data.active_translation_id || '';
        setActiveVersionId(active);

        // keep showing a preview while its version is still unfinished; once a
        // retry completes it, the server promotes it and it's the real text
        const pid = previewIdRef.current;
        const previewRow = pid ? list.find(v => v.id === pid) : null;
        const keepPreview = !!(previewRow && previewRow.status !== 'done');
        if (pid && !keepPreview) clearPreview();
        setViewingVersionId(keepPreview ? pid : active);

        // remember which version's pieces we'd show if you ask for them
        const activeRow = list.find(v => v.id === active);
        if (keepPreview && previewRow.is_chunked) {
          setChunkTranslationId(pid);
        } else if (activeRow && activeRow.is_chunked) {
          setChunkTranslationId(active);
        } else {
          setChunkTranslationId(prev => (prev && list.some(v => v.id === prev) ? prev : ''));
        }
      })
      .catch(err => console.log(err));
  };

  // No finished version and no English on the chapter, but a run that failed
  // partway DID write some: open it, rather than showing an empty page that
  // makes it look like nothing was saved.
  useEffect(() => {
    if (loading || translating || autoPreviewedRef.current) return;
    if (activeVersionId || translatedText.trim()) return;
    const partial = versions.find(v => v.status === 'error' && (v.char_count || 0) > 0);
    if (partial) {
      autoPreviewedRef.current = true;
      previewVersion(partial.id);
    }
  }, [loading, translating, versions, activeVersionId, translatedText]);

  const currentVersionIsChunked = () => {
    const v = versions.find(x => x.id === (viewingVersionId || activeVersionId));
    return !!(v && v.is_chunked);
  };

  const togglePiecesPanel = () => {
    const versionId = viewingVersionId || activeVersionId;
    if (!versionId) return;
    if (showChunks) {
      setShowChunks(false);
      setOpenChunkId('');
      return;
    }
    setShowChunks(true);
    setChunkTranslationId(versionId);
    fetchChunks(versionId);
  };

  // ---- background translation ----
  // The request returns instantly and the model keeps generating server-side.
  // A 130k-char chapter can take ~20 minutes, which no proxy will hold a
  // connection open for — that was the old "Network Error" while still being
  // billed. Now we just poll for progress.
  const pollStatus = (translationId) => {
    stopPolling();
    activeJobRef.current = translationId;
    pollRef.current = setInterval(() => {
      const q = translationId ? `&translation_id=${translationId}` : '';
      axios.get(`${API}/novels/${id}/chapters/${chapterId}/translation-status?token=${token}${q}`)
        .then(res => {
          const d = res.data;
          setJobStatus(d);
          if (d.is_chunked) fetchChunks(d.translation_id);
          if (d.status === 'done') {
            stopPolling();
            setTranslating(false);
            // the version you were previewing just finished — it's the real
            // chapter text now, so stop showing it as a preview
            if (previewIdRef.current === d.translation_id) clearPreview();
            if (d.content) {
              setTranslatedText(d.content);
              setTranslationModel(d.model || '');
              setSavedAt(new Date());
            }
            fetchVersions();
            setJobStatus(null);
          } else if (d.status === 'error') {
            stopPolling();
            setTranslating(false);
            setErrorMsg(`translate failed: ${d.error_message || 'unknown error'}`);
            // still failed, but a retry may have filled in more pieces
            if (previewIdRef.current && previewIdRef.current === d.translation_id) {
              loadPreviewText(d.translation_id);
            }
            fetchVersions();
            setJobStatus(null);
          }
        })
        .catch(err => console.log(err));
    }, 3000);
  };

  const resumeRunningJob = () => {
    axios.get(`${API}/novels/${id}/chapters/${chapterId}/translation-status?token=${token}`)
      .then(res => {
        if (res.data.status === 'running') {
          setTranslating(true);
          setJobStatus(res.data);
          pollStatus(res.data.translation_id);
        }
      })
      .catch(() => {});
  };

  const fetchChunks = (translationId) => {
    if (!translationId) return;
    axios.get(`${API}/novels/${id}/chapters/${chapterId}/translations/${translationId}/chunks?token=${token}`)
      .then(res => {
        setChunks(res.data.chunks || []);
        setChunkTranslationId(translationId);
      })
      .catch(err => console.log(err));
  };

  const chunkUrl = (chunkId) =>
    `${API}/novels/${id}/chapters/${chapterId}/translations/${chunkTranslationId}/chunks/${chunkId}`;

  const openChunk = (chunkId) => {
    if (openChunkId === chunkId) {
      setOpenChunkId('');
      setChunkDetail(null);
      return;
    }
    setOpenChunkId(chunkId);
    setChunkDetail(null);
    setChunkExtraInstruction('');
    axios.get(`${chunkUrl(chunkId)}?token=${token}`)
      .then(res => {
        setChunkDetail(res.data);
        setChunkSourceDraft(res.data.source_text || '');
        setChunkOutputDraft(res.data.content || '');
      })
      .catch(err => setErrorMsg(`couldn't load piece: ${describeError(err)}`));
  };

  const saveChunkOutput = () => {
    if (!openChunkId) return;
    setSavingChunkOutput(true);
    setErrorMsg('');
    axios.put(chunkUrl(openChunkId), { token, content: chunkOutputDraft })
      .then(() => {
        setChunkDetail(prev => ({ ...prev, content: chunkOutputDraft, status: 'done', warning: null }));
        fetchChunks(chunkTranslationId);
        // the chapter text is reassembled server-side, so pull it back in
        return axios.get(`${API}/novels/${id}/chapters/${chapterId}?token=${token}`);
      })
      .then(res => {
        if (res && res.data?.chapter) {
          setTranslatedText(res.data.chapter.translated_text || '');
          setSavedAt(new Date());
        }
        // if that was the last missing piece, the version is now finished and
        // active; otherwise refresh the partial text you're looking at
        fetchVersions();
        if (previewIdRef.current) loadPreviewText(previewIdRef.current);
      })
      .catch(err => setErrorMsg(`couldn't save piece output: ${describeError(err)}`))
      .finally(() => setSavingChunkOutput(false));
  };

  const saveChunkSource = () => {
    if (!openChunkId) return;
    setSavingChunk(true);
    axios.put(chunkUrl(openChunkId), { token, source_text: chunkSourceDraft })
      .then(() => setChunkDetail(prev => ({ ...prev, source_text: chunkSourceDraft })))
      .catch(err => setErrorMsg(`couldn't save piece: ${describeError(err)}`))
      .finally(() => setSavingChunk(false));
  };

  const retryChunk = (chunkId, withInstruction) => {
    if (!chunkTranslationId) return;
    setRetryingChunk(chunkId);
    setErrorMsg('');
    const payload = { token, model: selectedModel };
    if (withInstruction && chunkExtraInstruction.trim()) {
      payload.extra_instruction = chunkExtraInstruction.trim();
    }
    axios.post(`${chunkUrl(chunkId)}/retry`, payload)
      .then(() => {
        setTranslating(true);
        pollStatus(chunkTranslationId);
        if (openChunkId === chunkId) {
          setTimeout(() => openChunk(chunkId) || setOpenChunkId(chunkId), 0);
        }
      })
      .catch(err => setErrorMsg(`couldn't retry piece: ${describeError(err)}`))
      .finally(() => setRetryingChunk(''));
  };

  const translateChapter = () => {
    if (!sourceText.trim()) {
      setErrorMsg('paste the Korean text first');
      return;
    }
    if (!selectedModel) {
      setErrorMsg('pick a model first');
      return;
    }
    setErrorMsg('');
    setTranslating(true);
    setChunks([]);
    setJobStatus({ status: 'running', char_count: 0, model: selectedModel });
    // save the source text first so it's not lost, then kick off the job
    axios.put(`${API}/novels/${id}/chapters/${chapterId}`, {
      token, title, source_text: sourceText,
    })
      .then(() => axios.post(`${API}/novels/${id}/chapters/${chapterId}/translate`, {
        token,
        model: selectedModel,
        chunked: chunkedMode,
        chunk_size: chunkedMode ? Number(chunkSize) : null,
      }))
      .then(res => {
        if (res.data.chunked) {
          setChunkTranslationId(res.data.translation_id);
          setShowChunks(true);
          fetchChunks(res.data.translation_id);
        }
        pollStatus(res.data.translation_id);
      })
      .catch(err => {
        setTranslating(false);
        setJobStatus(null);
        setErrorMsg(`couldn't start translation: ${describeError(err)}`);
      });
  };

  const polishChapter = () => {
    if (!translatedText.trim()) {
      setErrorMsg('translate the chapter first — there\'s no English text to polish');
      return;
    }
    if (!selectedModel) {
      setErrorMsg('pick a model first');
      return;
    }
    setErrorMsg('');
    setTranslating(true);
    setChunks([]);
    setJobStatus({ status: 'running', char_count: 0, model: selectedModel });
    // save current text first
    axios.put(`${API}/novels/${id}/chapters/${chapterId}`, {
      token, title, source_text: sourceText, translated_text: translatedText,
    })
      .then(() => axios.post(`${API}/novels/${id}/chapters/${chapterId}/polish`, {
        token,
        model: selectedModel,
        chunked: true,
        chunk_size: 5000,
      }))
      .then(res => {
        if (res.data.chunked) {
          setChunkTranslationId(res.data.translation_id);
          setShowChunks(true);
          fetchChunks(res.data.translation_id);
        }
        pollStatus(res.data.translation_id);
      })
      .catch(err => {
        setTranslating(false);
        setJobStatus(null);
        setErrorMsg(`couldn't start polish: ${describeError(err)}`);
      });
  };

  // ---- version switching ----
  // Picking a FINISHED version activates it immediately — it becomes the text
  // used by the editor and by epub export. A failed one is only previewed.
  const switchToVersion = (versionId) => {
    if (!versionId || versionId === activeVersionId) return;
    setSwitchingVersion(true);
    setErrorMsg('');
    axios.post(
      `${API}/novels/${id}/chapters/${chapterId}/translations/${versionId}/activate`,
      { token }
    )
      .then(res => {
        clearPreview();
        setTranslatedText(res.data.translated_text || '');
        setTranslationModel(res.data.model || '');
        setActiveVersionId(versionId);
        setViewingVersionId(versionId);
        setView('english');
        setSavedAt(new Date());
      })
      .catch(err => {
        setViewingVersionId(previewIdRef.current || activeVersionId);
        setErrorMsg(`couldn't switch version: ${describeError(err)}`);
      })
      .finally(() => setSwitchingVersion(false));
  };

  // Wherever two paragraphs are only split by a single line break, add the
  // blank line. Words are never changed. Saved as a new version (shown as a
  // decimal, like a polish), so the one you had is still in the list.
  const fixSpacing = () => {
    if (!translatedText.trim()) {
      setErrorMsg("there's no English text yet");
      return;
    }
    setFixingSpacing(true);
    setErrorMsg('');
    setNotice('');
    // save what's on screen first, so your own edits are what gets spaced
    axios.put(`${API}/novels/${id}/chapters/${chapterId}`, {
      token, title, source_text: sourceText, translated_text: translatedText,
    })
      .then(() => axios.post(`${API}/novels/${id}/chapters/${chapterId}/fix-spacing`, { token }))
      .then(res => {
        const d = res.data;
        if (!d.changed) {
          setNotice('every paragraph already has a space after it — nothing to fix');
          return;
        }
        setTranslatedText(d.translated_text || '');
        setActiveVersionId(d.translation_id);
        setViewingVersionId(d.translation_id);
        setSavedAt(new Date());
        fetchVersions();
        setNotice(`added a space between paragraphs in ${d.added.toLocaleString()} place${d.added === 1 ? '' : 's'} — saved as a new version. the one before is still in the versions list.`);
      })
      .catch(err => setErrorMsg(`couldn't fix the spacing: ${describeError(err)}`))
      .finally(() => setFixingSpacing(false));
  };

  const deleteVersion = (versionId) => {
    axios.delete(`${API}/novels/${id}/chapters/${chapterId}/translations/${versionId}?token=${token}`)
      .then(() => {
        if (previewIdRef.current === versionId) clearPreview();
        fetchVersions();
      })
      .catch(err => setErrorMsg(`couldn't delete version: ${describeError(err)}`));
  };

  // Turn a stored model id ("anthropic/claude-opus-4.8") into something readable
  // ("Claude Opus 4.8"). Prefer the curated label from the model list; fall back
  // to prettifying the id so even a model no longer in the list stays legible.
  const prettyModel = (modelId) => {
    if (!modelId) return 'unknown model';
    const known = models.find(m => m.id === modelId);
    if (known && known.label) return known.label;
    const tail = modelId.includes('/') ? modelId.split('/').pop() : modelId;
    return tail
      .replace(/^claude-/, '')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Assign display numbers to every version. Translations get whole numbers in
  // the order they were made (oldest = V1). A polish gets a decimal off the
  // translation it came from (parent_version_id) — V2's first polish is V2.1,
  // its second is V2.2. Legacy polishes with no parent recorded fall back to
  // attaching to the most recent translation that preceded them.
  const versionNumbers = useMemo(() => {
    const chrono = [...versions].reverse(); // API sends newest-first
    const nums = {};
    let nextTranslation = 0;
    let lastTranslationId = null;
    const polishCounts = {};
    for (const v of chrono) {
      if (!v.is_polish) {
        nextTranslation += 1;
        nums[v.id] = `V${nextTranslation}`;
        lastTranslationId = v.id;
      } else {
        const parent = v.parent_version_id || lastTranslationId;
        const parentLabel = parent && nums[parent] ? nums[parent] : 'V?';
        polishCounts[parent] = (polishCounts[parent] || 0) + 1;
        nums[v.id] = `${parentLabel}.${polishCounts[parent]}`;
      }
    }
    return nums;
  }, [versions]);

  const formatVersionLabel = (v) => {
    const when = new Date(v.created_at + (v.created_at.endsWith('Z') ? '' : 'Z'));
    const stamp = isNaN(when.getTime())
      ? ''
      : when.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    const num = versionNumbers[v.id] || '';
    const model = prettyModel(v.model);
    const tag = v.is_polish ? ' · polished' : '';
    const head = v.model === 'spacing-fix' ? `${num} · spacing fixed` : `${num}${tag} · ${model}`;
    if (v.status === 'running') return `${head} — ${v.is_polish ? 'polishing' : 'translating'}…`;
    if (v.status === 'error') return `${head} — failed${(v.char_count || 0) > 0 ? ' (partial, tap to view)' : ''}`;
    return `${head} — ${stamp}`;
  };

  const createNextChapter = () => {
    setCreatingNext(true);
    setErrorMsg('');
    const currentIndex = chapterList.findIndex(c => c.id === chapterId);
    const existingNext = currentIndex >= 0 ? chapterList[currentIndex + 1] : null;

    // save current chapter first so nothing pasted gets lost
    axios.put(`${API}/novels/${id}/chapters/${chapterId}`, {
      token, title, source_text: sourceText, translated_text: translatedText,
    })
      .then(() => {
        if (existingNext) {
          // a next chapter already exists — just go there, don't create a duplicate
          navigate(`/novels/${id}/chapters/${existingNext.id}`);
          return null;
        }
        const nextNum = chapterList.length + 1;
        return axios.post(`${API}/novels/${id}/chapters`, {
          token,
          title: `Chapter ${nextNum}`,
          order: nextNum,
        });
      })
      .then(res => {
        if (res) {
          navigate(`/novels/${id}/chapters/${res.data.chapter.id}`);
        }
      })
      .catch(err => setErrorMsg(`couldn't go to next chapter: ${describeError(err)}`))
      .finally(() => setCreatingNext(false));
  };

  const goToPreviousChapter = () => {
    const currentIndex = chapterList.findIndex(c => c.id === chapterId);
    const existingPrev = currentIndex > 0 ? chapterList[currentIndex - 1] : null;
    if (!existingPrev) {
      setErrorMsg("you're on the first chapter — there's nothing before this one");
      return;
    }
    setNavigatingPrev(true);
    setErrorMsg('');
    // save current chapter first so nothing pasted gets lost
    axios.put(`${API}/novels/${id}/chapters/${chapterId}`, {
      token, title, source_text: sourceText, translated_text: translatedText,
    })
      .then(() => navigate(`/novels/${id}/chapters/${existingPrev.id}`))
      .catch(err => setErrorMsg(`couldn't go to previous chapter: ${describeError(err)}`))
      .finally(() => setNavigatingPrev(false));
  };

  // preview state, derived for the render
  const isPreviewing = !!previewId && previewText !== null;
  const previewRow = isPreviewing ? versions.find(v => v.id === previewId) : null;
  const previewPieces = isPreviewing && chunkTranslationId === previewId && chunks.length > 0
    ? { done: chunks.filter(c => c.status === 'done').length, total: chunks.length }
    : null;

  // find & replace helpers
  const activeText = view === 'korean'
    ? sourceText
    : (isPreviewing ? (previewText || '') : translatedText);
  const matchCount = findText
    ? (activeText.match(new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')) || []).length
    : 0;

  const replaceAll = () => {
    if (!findText || matchCount === 0) return;
    if (view !== 'korean' && isPreviewing) {
      setErrorMsg('this version is read-only while it\'s unfinished — finish its pieces or use it first, then replace');
      return;
    }
    const escaped = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(escaped, 'gi');
    if (view === 'korean') {
      setSourceText(prev => prev.replace(re, replaceText));
    } else {
      setTranslatedText(prev => prev.replace(re, replaceText));
    }
    // auto-save after a tick so state has flushed
    setTimeout(() => saveChapter(true), 50);
  };

  const s = {
    page: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f',
      fontFamily: "'DM Sans', sans-serif",
      fontWeight: 300,
      color: '#e8ddd0',
      display: 'flex', flexDirection: 'column',
      overflowY: 'hidden',
    },
    header: {
      padding: '16px 24px',
      display: 'flex', alignItems: 'center', gap: '12px',
      borderBottom: '1px solid rgba(201,147,90,0.06)',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
      flexShrink: 0, zIndex: 50,
    },
    backBtn: {
      display: 'flex', alignItems: 'center', gap: '8px',
      background: 'transparent', border: 'none', cursor: 'pointer',
      color: '#4a3d30', fontSize: '13px', fontFamily: "'DM Sans', sans-serif",
      flexShrink: 0,
    },
    titleInput: {
      flex: 1, background: 'transparent', border: 'none', outline: 'none',
      fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontStyle: 'italic',
      color: '#e8ddd0', minWidth: 0,
    },
    saveStatus: { fontSize: '11px', color: '#4a3d30', flexShrink: 0, whiteSpace: 'nowrap' },
    toolbar: {
      padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '10px',
      borderBottom: '1px solid rgba(201,147,90,0.06)', flexShrink: 0, flexWrap: 'wrap',
    },
    viewToggle: {
      display: 'flex', background: '#1e1825', borderRadius: '20px',
      padding: '3px', border: '1px solid rgba(201,147,90,0.1)',
    },
    viewToggleBtn: (active) => ({
      padding: '6px 16px', borderRadius: '16px', border: 'none',
      background: active ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent',
      color: active ? '#0e0b0f' : '#4a3d30',
      fontSize: '12px', cursor: 'pointer', fontWeight: active ? 400 : 300,
      fontFamily: "'DM Sans', sans-serif",
    }),
    modelSelect: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '10px', padding: '8px 12px', color: '#e8ddd0',
      fontSize: '12px', fontFamily: "'DM Sans', sans-serif", outline: 'none',
      maxWidth: '180px',
    },
    translateBtn: {
      padding: '8px 18px', borderRadius: '20px', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontSize: '12px', cursor: 'pointer', fontWeight: 400,
      fontFamily: "'DM Sans', sans-serif",
    },
    polishBtn: {
      padding: '8px 18px', borderRadius: '20px', border: 'none',
      background: 'linear-gradient(135deg, #5a3a8a, #8a5abf)', color: '#e8ddd0',
      fontSize: '12px', cursor: 'pointer', fontWeight: 400,
      fontFamily: "'DM Sans', sans-serif",
    },
    saveBtn: {
      padding: '8px 18px', borderRadius: '20px',
      border: '1px solid rgba(201,147,90,0.2)', background: 'transparent',
      color: '#c9935a', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
    },
    smallBtnFilled: {
      padding: '8px 18px', borderRadius: '20px', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontSize: '12px', cursor: 'pointer', fontWeight: 400,
      fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
    },
    errorBanner: {
      margin: '12px 24px 0', background: 'rgba(180,60,60,0.12)',
      border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px',
      padding: '10px 16px', fontSize: '12px', color: '#e0a0a0',
      flexShrink: 0,
    },
    versionSelect: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '10px', padding: '8px 12px', color: '#c9935a',
      fontSize: '12px', fontFamily: "'DM Sans', sans-serif", outline: 'none',
      maxWidth: '230px',
    },
    progressBanner: {
      margin: '12px 24px 0', background: 'rgba(201,147,90,0.08)',
      border: '1px solid rgba(201,147,90,0.25)', borderRadius: '12px',
      padding: '10px 16px', fontSize: '12px', color: '#c9935a',
      flexShrink: 0, lineHeight: 1.6,
    },
    progressHint: { color: '#4a3d30' },
    partialBanner: {
      margin: '12px 24px 0', background: 'rgba(200,106,106,0.08)',
      border: '1px solid rgba(200,106,106,0.28)', borderRadius: '12px',
      padding: '10px 16px', fontSize: '12px', color: '#e0b0a0',
      flexShrink: 0, lineHeight: 1.6,
      display: 'flex', flexDirection: 'column', gap: '6px',
    },
    partialHint: { color: '#8a7466', fontSize: '11px' },
    partialRow: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
    partialBtn: {
      background: 'transparent', border: '1px solid rgba(201,147,90,0.3)',
      borderRadius: '8px', color: '#c9935a', fontSize: '11px',
      padding: '6px 12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif",
    },
    chunkToggle: {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      fontSize: '12px', color: '#c9935a', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
    },
    chunkSizeSelect: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '10px', padding: '8px 10px', color: '#e8ddd0',
      fontSize: '12px', fontFamily: "'DM Sans', sans-serif", outline: 'none',
    },
    chunkPanel: {
      margin: '12px 24px 0', background: '#1e1825',
      border: '1px solid rgba(201,147,90,0.12)', borderRadius: '12px',
      padding: '12px 14px', flexShrink: 0, maxHeight: '46vh',
      display: 'flex', flexDirection: 'column',
    },
    chunkPanelTitle: {
      fontSize: '11px', letterSpacing: '0.1em', color: '#4a3d30',
      textTransform: 'uppercase', marginBottom: '10px',
    },
    chunkList: { overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' },
    chunkRow: { display: 'flex', alignItems: 'center', gap: '10px' },
    chunkDot: (status, warned) => ({
      width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
      background: warned ? '#d4a05a'
        : status === 'done' ? '#c9935a'
        : status === 'error' ? '#c86a6a'
        : status === 'running' ? '#e8ddd0'
        : 'rgba(201,147,90,0.2)',
    }),
    chunkIndex: {
      fontSize: '11px', color: '#4a3d30', width: '18px', flexShrink: 0,
    },
    chunkMeta: { flex: 1, minWidth: 0, cursor: 'pointer' },
    chunkStatusLine: {
      fontSize: '11px', color: '#e8ddd0', opacity: 0.75,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
    chunkWarning: { fontSize: '10px', color: '#d4a05a', marginTop: '2px' },
    chunkRetryBtn: {
      background: 'transparent', border: '1px solid rgba(201,147,90,0.25)',
      borderRadius: '8px', color: '#c9935a', fontSize: '11px',
      padding: '4px 10px', cursor: 'pointer', flexShrink: 0,
      fontFamily: "'DM Sans', sans-serif",
    },
    chunkOpenBtn: {
      background: 'transparent', border: '1px solid rgba(201,147,90,0.18)',
      borderRadius: '8px', color: '#4a3d30', fontSize: '11px',
      padding: '4px 10px', cursor: 'pointer', flexShrink: 0,
      fontFamily: "'DM Sans', sans-serif",
    },
    piecesBtn: {
      padding: '8px 14px', borderRadius: '20px',
      border: '1px solid rgba(201,147,90,0.2)', background: 'transparent',
      color: '#c9935a', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300, whiteSpace: 'nowrap',
    },
    piecesBtnActive: {
      padding: '8px 14px', borderRadius: '20px', border: 'none',
      background: 'rgba(201,147,90,0.18)', color: '#e8ddd0',
      fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300, whiteSpace: 'nowrap',
    },
    chunkDetail: {
      margin: '8px 0 12px 28px', padding: '10px 12px',
      background: '#120f18', border: '1px solid rgba(201,147,90,0.1)',
      borderRadius: '10px',
    },
    chunkDetailLabel: {
      fontSize: '10px', letterSpacing: '0.08em', color: '#4a3d30',
      textTransform: 'uppercase', margin: '8px 0 5px',
    },
    chunkDetailRow: {
      display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '6px',
    },
    chunkDetailHint: { fontSize: '10px', color: '#4a3d30' },
    chunkTextarea: {
      width: '100%', height: '120px', boxSizing: 'border-box', resize: 'vertical',
      background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.12)',
      borderRadius: '8px', padding: '8px 10px', color: '#e8ddd0',
      fontSize: '12px', lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif",
      outline: 'none',
    },
    chunkInput: {
      width: '100%', boxSizing: 'border-box',
      background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.12)',
      borderRadius: '8px', padding: '8px 10px', color: '#e8ddd0',
      fontSize: '12px', fontFamily: "'DM Sans', sans-serif", outline: 'none',
    },
    chunkSmallBtn: {
      background: 'transparent', border: '1px solid rgba(201,147,90,0.25)',
      borderRadius: '8px', color: '#c9935a', fontSize: '11px',
      padding: '6px 12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif",
    },
    chunkSmallBtnFilled: {
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', border: 'none',
      borderRadius: '8px', color: '#0e0b0f', fontSize: '11px',
      padding: '6px 12px', cursor: 'pointer', fontWeight: 400,
      fontFamily: "'DM Sans', sans-serif",
    },
    searchBtn: (active) => ({
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '34px', height: '34px', borderRadius: '50%', border: 'none',
      background: active ? 'rgba(201,147,90,0.18)' : 'transparent',
      cursor: 'pointer', flexShrink: 0, padding: 0,
    }),
    findBar: {
      padding: '10px 24px', display: 'flex', alignItems: 'center', gap: '10px',
      borderBottom: '1px solid rgba(201,147,90,0.06)', flexShrink: 0,
      background: '#1e1825', flexWrap: 'wrap',
    },
    findInput: {
      background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '8px', padding: '7px 12px', color: '#e8ddd0',
      fontSize: '13px', fontFamily: "'DM Sans', sans-serif", outline: 'none',
      width: '180px',
    },
    findCount: {
      fontSize: '11px', color: '#4a3d30', flexShrink: 0, minWidth: '60px',
    },
    replaceAllBtn: {
      padding: '7px 16px', borderRadius: '16px', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontSize: '12px', cursor: 'pointer', fontWeight: 400,
      fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
    },
    replaceAllBtnDisabled: {
      padding: '7px 16px', borderRadius: '16px', border: 'none',
      background: 'rgba(201,147,90,0.15)', color: '#4a3d30',
      fontSize: '12px', cursor: 'default', fontWeight: 400,
      fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap',
    },
    body: {
      flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    paneLabel: {
      padding: '14px 24px 6px', fontSize: '11px', letterSpacing: '0.12em',
      color: '#4a3d30', textTransform: 'uppercase', flexShrink: 0,
    },
    textarea: {
      flex: 1, background: 'transparent', border: 'none', outline: 'none',
      resize: 'none', padding: '4px 24px 24px', color: '#e8ddd0',
      fontSize: '15px', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif",
      fontWeight: 300, maxWidth: '760px', width: '100%', margin: '0 auto',
      boxSizing: 'border-box',
    },
  };

  if (loading) return <div style={s.page} />;

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />

      {/* HEADER */}
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => navigate(`/novels/${id}`)}>← back</button>
        <input
          style={s.titleInput}
          value={title}
          onChange={e => setTitle(e.target.value)}
          onBlur={() => saveChapter(true)}
          placeholder="chapter title"
        />
        <span style={s.saveStatus}>
          {saving ? 'saving…' : savedAt ? `saved ${savedAt.toLocaleTimeString()}` : ''}
        </span>
      </header>

      {/* TOOLBAR */}
      <div style={s.toolbar}>
        <div style={s.viewToggle}>
          <button style={s.viewToggleBtn(view === 'korean')} onClick={() => setView('korean')}>korean</button>
          <button style={s.viewToggleBtn(view === 'english')} onClick={() => setView('english')}>english</button>
        </div>
        <select
          style={s.modelSelect}
          value={selectedModel}
          onChange={e => {
            setSelectedModel(e.target.value);
            localStorage.setItem('lastTranslationModel', e.target.value);
          }}
        >
          {models.map(m => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
        <label style={s.chunkToggle} title="split this chapter into pieces and translate them one at a time">
          <input
            type="checkbox"
            checked={chunkedMode}
            onChange={e => setChunkedMode(e.target.checked)}
            style={{ accentColor: '#c9935a' }}
          />
          long chapter
        </label>
        {chunkedMode && (
          <select
            style={s.chunkSizeSelect}
            value={chunkSize}
            onChange={e => setChunkSize(Number(e.target.value))}
            title="characters per piece — smaller is safer for models with small output limits"
          >
            <option value={5000}>~5k per piece</option>
            <option value={10000}>~10k per piece</option>
            <option value={15000}>~15k per piece</option>
          </select>
        )}
        <button style={s.translateBtn} onClick={translateChapter} disabled={translating}>
          {translating ? 'translating…' : '✨ translate'}
        </button>
        <button style={s.polishBtn} onClick={polishChapter} disabled={translating} title="polish the current translation">
          {translating ? '…' : '💎'}
        </button>
        {versions.length > 0 && (
          <select
            style={s.versionSelect}
            value={viewingVersionId}
            disabled={switchingVersion}
            onChange={e => {
              const vid = e.target.value;
              const row = versions.find(v => v.id === vid);
              // back to the placeholder, or back to the version already in use
              if (!row || vid === activeVersionId) {
                clearPreview();
                setViewingVersionId(vid);
                return;
              }
              if (row.status === 'done') {
                setViewingVersionId(vid);
                switchToVersion(vid);
              } else {
                // failed: look, don't activate
                previewVersion(vid);
              }
            }}
            title="previous translations — picking a finished one makes it the version used for export; a failed one opens read-only"
          >
            {!activeVersionId && <option value="">— pick a version —</option>}
            {versions.map(v => (
              <option key={v.id} value={v.id} disabled={v.status === 'running'}>
                {formatVersionLabel(v)}
              </option>
            ))}
          </select>
        )}
        {currentVersionIsChunked() && (
          <button
            style={showChunks ? s.piecesBtnActive : s.piecesBtn}
            onClick={togglePiecesPanel}
            title="show the individual pieces of this translation"
          >
            {showChunks ? 'hide pieces' : '⛭ pieces'}
          </button>
        )}
        {view === 'english' && !isPreviewing && (
          <button
            style={s.saveBtn}
            onClick={fixSpacing}
            disabled={fixingSpacing || translating || !translatedText.trim()}
            title="add the blank line between paragraphs wherever it's missing — saved as a new version"
          >
            {fixingSpacing ? 'fixing…' : '¶ fix spacing'}
          </button>
        )}
        <button
          style={s.searchBtn(showFind)}
          onClick={() => setShowFind(v => !v)}
          title="find & replace"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c9935a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
        <button style={s.saveBtn} onClick={() => saveChapter(false)} disabled={saving}>
          {saving ? 'saving…' : 'save'}
        </button>
        <button style={s.saveBtn} onClick={goToPreviousChapter} disabled={navigatingPrev} title="previous chapter">
          {navigatingPrev ? '…' : '←'}
        </button>
        <button style={s.smallBtnFilled} onClick={createNextChapter} disabled={creatingNext} title="next chapter">
          {creatingNext ? '…' : '→'}
        </button>
        {translationModel && translationModel !== 'spacing-fix' && (
          <span style={s.saveStatus}>last translated with {translationModel}</span>
        )}
      </div>

      {showFind && (
        <div style={s.findBar}>
          <input
            style={s.findInput}
            value={findText}
            onChange={e => setFindText(e.target.value)}
            placeholder="find…"
            autoFocus
          />
          <input
            style={s.findInput}
            value={replaceText}
            onChange={e => setReplaceText(e.target.value)}
            placeholder="replace with…"
          />
          <span style={s.findCount}>
            {findText ? `${matchCount} found` : ''}
          </span>
          <button
            style={matchCount > 0 ? s.replaceAllBtn : s.replaceAllBtnDisabled}
            onClick={replaceAll}
            disabled={matchCount === 0}
          >
            replace all
          </button>
        </div>
      )}

      {jobStatus && jobStatus.status === 'running' && (
        <div style={s.progressBanner}>
          translating with {jobStatus.model || 'model'}…{' '}
          {jobStatus.char_count > 0
            ? `${jobStatus.char_count.toLocaleString()} characters so far`
            : 'waiting for the first words'}
          <span style={s.progressHint}>
            {' '}— this runs on the server, so you can close this page and come back
          </span>
        </div>
      )}

      {isPreviewing && previewRow && (
        <div style={s.partialBanner}>
          <div>
            viewing {versionNumbers[previewRow.id] || 'this version'} —{' '}
            {previewRow.status === 'running'
              ? 'a retry is running, so this may still change.'
              : 'this run failed partway, so it isn\'t the chapter\'s text yet.'}
            {previewPieces && ` ${previewPieces.done} of ${previewPieces.total} pieces translated.`}
          </div>
          <div style={s.partialHint}>
            {previewRow.is_chunked
              ? 'retry the failed piece in the list below (open it to switch model or add an instruction). once every piece is done, this version becomes the chapter\'s text on its own.'
              : 'this run wasn\'t split into pieces, so the way to finish it is to translate again — tick "long chapter" first.'}
          </div>
          {previewRow.status !== 'running' && (previewText || '').trim() && (
            <div style={s.partialRow}>
              <button style={s.partialBtn} onClick={activatePreview} disabled={switchingVersion}>
                {switchingVersion ? 'switching…' : 'use this partial text anyway'}
              </button>
              <span style={s.partialHint}>
                it becomes the chapter's text and goes into the epub with the missing part left out
              </span>
            </div>
          )}
        </div>
      )}

      {chunks.length > 0 && (showChunks || translating) && (
        <div style={s.chunkPanel}>
          <div style={s.chunkPanelTitle}>
            pieces — {chunks.filter(c => c.status === 'done').length} of {chunks.length} done
            {chunks.some(c => c.status === 'error') && ' · failed pieces can be retried individually'}
          </div>
          <div style={s.chunkList}>
            {chunks.map(c => (
              <div key={c.id}>
                <div style={s.chunkRow}>
                  <span style={s.chunkDot(c.status, !!c.warning)} />
                  <span style={s.chunkIndex}>{c.index + 1}</span>
                  <div style={s.chunkMeta} onClick={() => openChunk(c.id)}>
                    <div style={s.chunkStatusLine}>
                      {c.status === 'done' && `${c.char_count.toLocaleString()} chars`}
                      {c.status === 'running' && 'translating…'}
                      {c.status === 'pending' && 'waiting'}
                      {c.status === 'error' && (c.error_message || 'failed')}
                      {c.model ? ` · ${c.model}` : ''}
                    </div>
                    {c.warning && <div style={s.chunkWarning}>⚠ {c.warning}</div>}
                  </div>
                  {c.status !== 'running' && c.status !== 'pending' && (
                    <>
                      <button style={s.chunkOpenBtn} onClick={() => openChunk(c.id)}>
                        {openChunkId === c.id ? 'close' : 'open'}
                      </button>
                      <button
                        style={s.chunkRetryBtn}
                        onClick={() => retryChunk(c.id, false)}
                        disabled={retryingChunk === c.id}
                      >
                        {retryingChunk === c.id ? '…' : 'retry'}
                      </button>
                    </>
                  )}
                </div>

                {openChunkId === c.id && (
                  <div style={s.chunkDetail}>
                    {!chunkDetail ? (
                      <div style={s.chunkDetailLabel}>loading…</div>
                    ) : (
                      <>
                        <div style={s.chunkDetailLabel}>
                          korean source for this piece — edit it if the model choked on something
                        </div>
                        <textarea
                          style={s.chunkTextarea}
                          value={chunkSourceDraft}
                          onChange={e => setChunkSourceDraft(e.target.value)}
                        />
                        <div style={s.chunkDetailRow}>
                          <button style={s.chunkSmallBtn} onClick={saveChunkSource} disabled={savingChunk}>
                            {savingChunk ? 'saving…' : 'save source'}
                          </button>
                          <span style={s.chunkDetailHint}>
                            only this piece's copy changes — your chapter's Korean is untouched
                          </span>
                        </div>

                        <div style={s.chunkDetailLabel}>extra instruction for the retry (optional)</div>
                        <input
                          style={s.chunkInput}
                          value={chunkExtraInstruction}
                          onChange={e => setChunkExtraInstruction(e.target.value)}
                          placeholder="e.g. translate every paragraph fully, do not summarise"
                        />
                        <div style={s.chunkDetailRow}>
                          <button
                            style={s.chunkSmallBtnFilled}
                            onClick={() => retryChunk(c.id, true)}
                            disabled={retryingChunk === c.id}
                          >
                            {retryingChunk === c.id ? 'retrying…' : `retry with ${selectedModel || 'model'}`}
                          </button>
                        </div>

                        <div style={s.chunkDetailLabel}>
                          what came back — editable ({(chunkOutputDraft || '').length.toLocaleString()} chars)
                        </div>
                        <textarea
                          style={s.chunkTextarea}
                          value={chunkOutputDraft}
                          onChange={e => setChunkOutputDraft(e.target.value)}
                          placeholder="the model's output for this piece — edit it directly if you want"
                        />
                        <div style={s.chunkDetailRow}>
                          <button
                            style={s.chunkSmallBtn}
                            onClick={saveChunkOutput}
                            disabled={savingChunkOutput}
                          >
                            {savingChunkOutput ? 'saving…' : 'save output'}
                          </button>
                          <span style={s.chunkDetailHint}>
                            saving rebuilds the full chapter from all pieces
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {notice && (
        <div style={{ ...s.progressBanner, cursor: 'pointer' }} onClick={() => setNotice('')} title="tap to close">
          {notice}
        </div>
      )}

      {errorMsg && <div style={s.errorBanner}>⚠ {errorMsg}</div>}

      {/* BODY — single pane, toggled */}
      <div style={s.body}>
        {view === 'korean' ? (
          <>
            <div style={s.paneLabel}>korean (source)</div>
            <textarea
              style={s.textarea}
              value={sourceText}
              onChange={e => setSourceText(e.target.value)}
              onBlur={() => saveChapter(true)}
              placeholder="paste the Korean text here…"
            />
          </>
        ) : isPreviewing ? (
          <>
            <div style={s.paneLabel}>
              english — {versionNumbers[previewId] || 'version'} (partial, read-only)
            </div>
            <textarea
              style={{ ...s.textarea, opacity: 0.85 }}
              value={previewText || ''}
              readOnly
              placeholder={previewLoading ? 'loading…' : 'this version has no text saved'}
            />
          </>
        ) : (
          <>
            <div style={s.paneLabel}>english (translation — editable)</div>
            <textarea
              style={s.textarea}
              value={translatedText}
              onChange={e => setTranslatedText(e.target.value)}
              onBlur={() => saveChapter(true)}
              placeholder="translation will appear here after you hit translate…"
            />
          </>
        )}
      </div>
    </div>
  );
}
